import React, { useMemo } from "react";
import { colors2, SkeletonLoader } from "oolib";
import { UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { SidePanel } from "@/components/UI/SidePanel";
import { useGetTopicClusteringTreeCounts } from "@/utils/react-query-hooks/aiAnnotation";
import { CardGridLayout } from "@/components/analysisModules/TopicClusteringTreeView/CardGridLayout";
import { toCardTree } from "./toCardTree";
import { activeFiltersFor, previousWindowFiltersFor } from "./scopeFilters";

/**
 * The Topics page's topic cards, narrowed to one signal category, in a side
 * panel — what a signal category card on the Overview opens. The first
 * level of the category stack; a topic card opens the topic's drilldown
 * beside it (onOpenTopic).
 *
 * Owns the one request the grid needs: the tree counts over the scope
 * (the period plus the category's signals). The "vs last period" chips are
 * requested by the layout that draws them, as on the page.
 *
 *   isOpen, width
 *   categoryDisplay      the category's name, for the panel title
 *   scope                { period, comparePeriod, extraFilters, topicClusteringLens, dateFieldMap }
 *   titleByContentType, onOpenTopic(nodeId)
 *   …shell               the SidePanelStack's props for this level
 *
 * Must render inside a SignalCategoriesProvider (the cards read the theme).
 */
const BODY_PAD = "16px 20px 24px";

const BodySkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {[0, 1, 2].map((i) => (
      <SkeletonLoader key={i} style={{ height: "13rem", borderRadius: 8 }} />
    ))}
  </div>
);

export const CategoryTopicsPanel = ({ isOpen, width, categoryDisplay, scope, titleByContentType, onOpenTopic, ...shell }) => {
  const { period, comparePeriod, extraFilters, topicClusteringLens } = scope;
  const activeFilters = useMemo(() => activeFiltersFor(period, extraFilters), [period, extraFilters]);
  const { data: counts, isLoading } = useGetTopicClusteringTreeCounts({
    topicClusteringLens,
    activeFilters,
    enabled: isOpen && !!period,
  });
  const tree = useMemo(() => toCardTree(counts), [counts]);

  // The "vs last period" chips compare with the selection's comparison period.
  const prevWindow = useMemo(
    () => (comparePeriod ? { from: comparePeriod.from, to: comparePeriod.to, label: comparePeriod.display } : null),
    [comparePeriod]
  );
  const prevScope = useMemo(
    () => (prevWindow ? { topicClusteringLens, activeFilters: previousWindowFiltersFor(prevWindow, extraFilters) } : null),
    [prevWindow, topicClusteringLens, extraFilters]
  );

  return (
    <SidePanel
      {...shell}
      isOpen={isOpen}
      width={width}
      title={<UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>{`${categoryDisplay || "Category"} · Topics · ${period?.display || ""}`}</UI_CAPTION_SEMIBOLD_DF>}
      bodyStyle={{ background: colors2.grey3, padding: BODY_PAD }}
    >
      {isOpen && (isLoading || !tree) && <BodySkeleton />}
      {isOpen && tree && tree.tree.length === 0 && !isLoading && (
        <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>No topics in this category for {period?.display || "this period"}.</UI_CAPTION_SEMIBOLD_DF>
      )}
      {isOpen && tree && tree.tree.length > 0 && (
        <CardGridLayout
          tree={tree}
          signalMetaById={counts?.signalMetaById || {}}
          titleByContentType={titleByContentType}
          selectedBucket={null}
          onSelectBucket={(bucket) => onOpenTopic(bucket.nodeId)}
          onRowClick={() => {}}
          openRowNodeId={null}
          period={period}
          prevWindow={prevWindow}
          prevScope={prevScope}
          gridMinWidth="26rem"
        />
      )}
    </SidePanel>
  );
};
